import { useCallback } from 'react'
import { useActiveWeb3React } from './web3'
import { Contract } from '@ethersproject/contracts'
import { parseEther } from '@ethersproject/units'
import { ChainId } from 'constants/chains'

interface ChainConfig {
  honeyCheckerAddress: string
  simulationAddress: string
}

const CHAIN_CONFIGS: { [chainId in ChainId]?: ChainConfig } = {
  [ChainId.ABSTRACT]: {
    honeyCheckerAddress: '0x1CbC9fC470e9B6E1D524531a97139f0EE85551bd',
    simulationAddress: '0x7E035Fb048a31e0481b88074557415b1C187242B'
  },
}

const HONEY_CHECKER_ABI = [
  'function honeyCheck(address targetTokenAddress, address idexRouterAddres) external payable returns (tuple(uint256 buyResult, uint256 tokenBalance2, uint256 sellResult, uint256 buyCost, uint256 sellCost, uint256 expectedAmount))'
]

export interface HoneyCheckResult {
  buyTax: number | null
  sellTax: number | null
  isHoneypot: boolean
  hasLiquidity: boolean
}

export function useHoneyPotChecker() {
  const { library, chainId } = useActiveWeb3React()

  const checkHoneypot = useCallback(
    async (tokenAddress: string, routerAddress: string): Promise<HoneyCheckResult> => {
      if (!library || !chainId) {
        throw new Error('Web3 not initialized')
      }

      const chainConfig = CHAIN_CONFIGS[chainId]
      const enabled = !!chainConfig

      if (!enabled) {
        return {
          buyTax: null,
          sellTax: null,
          isHoneypot: false,
          hasLiquidity: false
        }
      }

      try {
        const honeyCheckerContract = new Contract(
          chainConfig!.honeyCheckerAddress,
          HONEY_CHECKER_ABI,
          library
        )

        const buyAmount = parseEther("0.001")
        const result = await honeyCheckerContract.callStatic.honeyCheck(
          tokenAddress,
          routerAddress,
          { 
            from: chainConfig!.simulationAddress,
            value: buyAmount,
            gasLimit: 4500000
          }
        )

        const buyFeePercentage = (1 - (Number(result.buyResult) / Number(result.expectedAmount))) * 100
        const sellFeePercentage = (1 - (Number(result.sellResult) / Number(buyAmount))) * 100 - buyFeePercentage
        const hasLiquidity = Number(result.expectedAmount) > 0
        const isHoneypot = hasLiquidity && Number(result.sellResult) === 0

        console.log('Raw results:', JSON.stringify({
          buyResult: result.buyResult.toString(),
          sellResult: result.sellResult.toString(),
          expectedAmount: result.expectedAmount.toString(),
          buyFeePercentage,
          sellFeePercentage,
          hasLiquidity,
          isHoneypot
        }))

        return {
          buyTax: hasLiquidity ? (buyFeePercentage < 1 ? 0 : Math.max(0, buyFeePercentage)) : null,
          sellTax: hasLiquidity ? (sellFeePercentage < 1 ? 0 : Math.max(0, sellFeePercentage)) : null,
          isHoneypot,
          hasLiquidity
        }
      } catch (error) {
        console.error('Honeypot check failed:', error)
        return {
          buyTax: null,
          sellTax: null,
          isHoneypot: false,
          hasLiquidity: false
        }
      }
    },
    [library, chainId]
  )

  return { checkHoneypot }
}
